import Toolbar from "quill/modules/toolbar";
import { forwardRef, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { ControllerRenderProps } from "react-hook-form";
import ReactQuill, { ReactQuillProps } from "react-quill";
import { Dropdown, OptionType } from "../Dropdown";

type Props = Omit<
    ReactQuillProps &
        ControllerRenderProps & {
            height?: number;
            placeholder?: string;
            useVariables?: boolean;
        },
    "onBlur"
>;

export const VARIABLE_DROPDOWN_OPTIONS: OptionType<string>[] = [
    { label: "Ime i prezime", value: "ime prezime" },
    { label: "PG", value: "PG" },
    { label: "MIBPG", value: "MIBPG" },
];

const modules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ font: [] }],
        ["bold", "italic", "underline"],
        ["link"],
        [{ color: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
    ],
};

export const TextArea = forwardRef<ReactQuill, Props>((props: Props, ref) => {
    const { height, useVariables, placeholder, onChange, ...quillOptions } = props;
    const quillRef = useRef<ReactQuill | null>(null);

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        if (useVariables) {
            const toolbar = editor.getModule("toolbar") as Toolbar;
            const toolbarContainer = toolbar.container;
            const dropdownContainer = document.createElement("span");
            dropdownContainer.className = "ql-variables";

            const rootElement = document.createElement("div");
            dropdownContainer.appendChild(rootElement);

            ReactDOM.render(
                <Dropdown
                    styles={{
                        placeholder: () => ({
                            fontWeight: 500,
                            color: getComputedStyle(document.documentElement).getPropertyValue(
                                "--color-secondary-dark-gray"
                            ),
                        }),
                        container: provided => ({
                            ...provided,
                        }),
                        control: provided => ({
                            ...provided,
                        }),
                    }}
                    menuPlacement="auto"
                    options={VARIABLE_DROPDOWN_OPTIONS}
                    placeholder="Varijable"
                    onChange={selectedValue => {
                        editor.focus();
                        insertVariable(selectedValue!.value);
                    }}
                    value={null}
                    maxMenuHeight={200}
                    size="medium"
                />,
                rootElement
            );

            toolbarContainer?.appendChild(dropdownContainer);
        }
    }, [useVariables]);

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        if (height) {
            editor.root.style.height = `${height}px`;
        }
    }, [height]);

    const insertVariable = (variable: string) => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        const cursorPosition = editor.getSelection()?.index ?? 0;
        editor.focus();
        editor.insertText(cursorPosition, `{{${variable}}}`);
    };

    return (
        <ReactQuill
            {...quillOptions}
            ref={el => {
                quillRef.current = el;
                if (typeof ref === "function") {
                    ref(el);
                } else if (ref) {
                    (ref as any).current = el;
                }
            }}
            onChange={value => {
                const cleanedValue = value === "<p><br></p>" ? "" : value;
                onChange(cleanedValue);
            }}
            modules={modules}
            placeholder={placeholder}
        />
    );
});
